import { Loader } from "@/components/ProtectedRoute"
import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "@/components/ui/use-toast";
import { SpecificationForm } from "@/components/forms/specifications.form"
import { editSpecification, getSpecificaiton, getUser } from "@/service/weexpertService"
import { format } from "date-fns";

export const SpecificationEditPage = (props) => {
    const { id } = useParams()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { data: specification, isLoading, isFetched ,refetch } = useQuery({
        queryKey: ['specification', id], queryFn: () => {
            return getSpecificaiton(id)
        }
    })

    const { data: user, isLoading: isUserLoading } = useQuery({ queryKey: 'userMe', queryFn: getUser, retry: false });

    const onSaved = (
        contract,
        description,
        functionality,
        signatory,
        stages,
        redirectFromAutosave = false
    ) => {
        if (!contract
            || !description
            || !functionality || stages.length < 1) {
            toast({
                title: t("alerts:toast_error_invoice_title"),
                description: t("alerts:toast_error_invoice_description"),
                variant: t("destructive")
            })
            return
        }
        return editSpecification({
            specificationId: id,
            contract: contract.id,
            description,
            functionality,
            signatory,
            stages: stages.map(e => {
                const processedId = typeof e.id == 'number' ? e.id : null
                return {
                    ...e,
                    id: processedId,
                    order: +e.order,
                    grossAmount: +e.grossAmount,
                    endDate: e.endDate ? format(new Date(e.endDate), 'yyyy-MM-dd') : null
                }
            })
        })
            .then(response => {
                if (response.status === 200) {
                    toast({
                        title: t("alerts:success"),
                        description: t("alerts:success_change_data"),
                    })
                    if (!redirectFromAutosave) {
                        navigate('/specifications/')
                    }
                }
        return response;
            })
            .catch((error) => {
                // Check if this is the specific error about paid bills
                if (error.response?.data?.message && 
                    error.response.data.message.includes("Cannot delete specification items that have paid bills")) {
                    
                    try {
                        // Parse the error message to extract details
                        const errorMessage = error.response.data.message;
                        const jsonStart = errorMessage.indexOf('[');
                        const jsonEnd = errorMessage.lastIndexOf(']') + 1;
                        
                        if (jsonStart !== -1 && jsonEnd !== -1) {
                            const itemsDetails = JSON.parse(errorMessage.substring(jsonStart, jsonEnd));
                            
                            // Create user-friendly message
                            const itemDescriptions = itemsDetails.map(item => item.specificationItemDescription).join(', ');
                            const billIds = itemsDetails.flatMap(item => item.paidBillIds).join(', ');
                            
                            toast({
                                title: t("alerts:error_specification_paid_bills_title"),
                                description: t("alerts:error_specification_paid_bills_description", { 
                                    items: itemDescriptions, 
                                    bills: billIds 
                                }),
                                variant: "destructive"
                            });
                        } else {
                            // Fallback if JSON parsing fails
                            toast({
                                title: t("alerts:error_specification_paid_bills_title"),
                                description: t("alerts:error_specification_paid_bills_fallback"),
                                variant: "destructive"
                            });
                        }
                    } catch (parseError) {
                        // Fallback if parsing fails completely
                        toast({
                            title: t("alerts:error_specification_paid_bills_title"),
                            description: t("alerts:error_specification_paid_bills_fallback"),
                            variant: "destructive"
                        });
                    }
                } else {
                    // Generic error handling for other errors
                    toast({
                        title: t("alerts:toast_error_invoice_title"),
                        description: t("alerts:toast_error_invoice_description"),
                        variant: "destructive"
                    });
                }
            })
      .finally(() => {
        refetch()
      }
      );
    }
    if (isLoading || !isFetched) {
        return <Loader />
    }
    if (isUserLoading) {
        return <Loader />
    }
    if (user.data.roles === 'CLIENT') {
        navigate('/404')
    }

    return (
        <SpecificationForm
            onSaved={onSaved}
            action="edit"
            initialData={specification.data}
            onFetched={refetch}
        />
    );
}
